import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Bot, BotStatus } from '@/types/trader';
import { deleteShadowBot } from '@/services/shadow-bot-service';

type ViewMode = 'grid' | 'list';

const VIEW_MODE_KEY = 'my-bots-view-mode';

export const useBotManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [botToDelete, setBotToDelete] = useState<Bot | null>(null);
  const [isDeployDialogOpen, setIsDeployDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_KEY);
    return (saved === 'grid' || saved === 'list') ? saved : 'grid';
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const { data: bots, isLoading } = useQuery({
    queryKey: ['userBots'],
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');
      
      const { data: botsData, error: botsError } = await supabase
        .from('bots')
        .select('*')
        .eq('owner_id', user.id)
        .eq('type', 'user_bot')
        .neq('type', 'shadow_bot') // Exclude shadow bots
        .order('created_date', { ascending: false });
      
      if (botsError) throw botsError;
      
      const botsWithCounts = await Promise.all(botsData.map(async (bot) => {
        const { count, error } = await supabase
          .from('bot_accounts')
          .select('id', { count: 'exact', head: true })
          .eq('bot_id', bot.id)
          .eq('user_id', user.id);
        
        if (error) {
          console.error('Error counting bot accounts:', error);
          return { ...bot, connected_accounts_count: 0 };
        }
        
        return { ...bot, connected_accounts_count: count || 0 };
      }));
      
      return botsWithCounts as Bot[];
    },
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: true,
  });

  const toggleBotStatus = useMutation({
    mutationFn: async ({ botId, newStatus }: { botId: string, newStatus: BotStatus }) => {
      const { data, error } = await supabase
        .from('bots')
        .update({ status: newStatus })
        .eq('id', botId)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userBots'] });
      toast({
        title: `Bot ${data.status === 'active' ? 'Started' : 'Paused'}`,
        description: `${data.name} is now ${data.status === 'active' ? 'running' : 'paused'}.`,
      });
    },
    onError: (error) => {
      console.error('Error updating bot status:', error);
      toast({
        title: 'Failed to update bot status',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const deleteBot = useMutation({
    mutationFn: async (botId: string) => {
      // Delete shadow bot first
      await deleteShadowBot(botId);
      
      const { error } = await supabase
        .from('bots')
        .delete()
        .eq('id', botId);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['userBots'],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({ 
        queryKey: ['resourceUsage', user?.id],
        refetchType: 'active',
      });
      
      toast({
        title: 'Bot deleted',
        description: 'The bot was successfully deleted.',
      });
      setBotToDelete(null);
    },
    onError: (error) => {
      console.error('Error deleting bot:', error);
      toast({
        title: 'Failed to delete bot',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const filteredBots = bots?.filter(bot => {
    const matchesSearch = 
      bot.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bot.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bot.id.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesTab = 
      activeTab === 'all' || 
      (activeTab === 'active' && bot.status === 'active') ||
      (activeTab === 'inactive' && bot.status === 'inactive');
      
    return matchesSearch && matchesTab;
  }) || [];

  useEffect(() => {
    if (searchQuery && filteredBots.length === 0 && !isLoading && bots?.length) {
      toast({
        title: 'No matches found',
        description: `No bots match your search for "${searchQuery}"`,
      });
    }
  }, [searchQuery, filteredBots.length, isLoading, bots?.length, toast]);

  const handleViewDetails = (botId: string) => {
    navigate(`/bots/${botId}`);
  };

  const handleToggleStatus = (botId: string, currentStatus: BotStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    toggleBotStatus.mutate({ botId, newStatus });
  };

  const handleDeleteClick = (bot: Bot) => {
    setBotToDelete(bot);
  };

  const handleDeleteConfirm = () => {
    if (botToDelete) {
      deleteBot.mutate(botToDelete.id);
    }
  };

  const handleOpenDeployDialog = () => {
    setIsDeployDialogOpen(true);
  };

  const handleCloseDeployDialog = () => {
    setIsDeployDialogOpen(false);
  };

  const handleSetViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  return {
    bots,
    isLoading,
    filteredBots,
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    botToDelete,
    setBotToDelete,
    isDeployDialogOpen,
    viewMode,
    setViewMode: handleSetViewMode,
    deleteBot,
    handleViewDetails,
    handleToggleStatus,
    handleDeleteClick,
    handleDeleteConfirm,
    handleOpenDeployDialog,
    handleCloseDeployDialog
  };
};
